import { Dropdown } from 'floating-vue';
import { copyToClipboard } from '../../helpers/functions';

const offsetLeft = (node) => {
    var offset = node.offsetLeft;
    if (node.offsetParent) {
        offset += offsetLeft(node.offsetParent);
    }
    return offset;
};

const formatDuration = (seconds) => {
    let secondsNum = parseInt(seconds, 10);
    let _hours = Math.floor(secondsNum / 3600);
    let _minutes = Math.floor(secondsNum / 60) % 60;
    let _seconds = secondsNum % 60;

    return [_hours, _minutes, _seconds]
        .map((v) => (v < 10 ? '0' + v : v))
        .filter((v, i) => v !== '00' || i > 0)
        .join(':');
};

const PLAYBACK_RATES = [1, 1.1, 1.2, 1.3, 1.5, 1.7, 2.0];

// Shared audio-playback logic (state, timing, seeking, rate, volume,
// localStorage time persistence) behind the <audio> element. Consumed by
// PlayerSimple.vue (the default player look, used across the whole app) and
// by PartsPlayerFooter.vue (the professional-prooflistening embedded
// reviewer footer, packages/core/editor/components/PartsPlayer/), which
// needs its own template to match an exact design mock but must not
// duplicate this behavior. Any component using this mixin must render, with
// matching ref names: ref="audio" on the <audio> element, ref="bar" on the
// progress track, ref="progress" / ref="loaded" on the fill bars, and
// ref="handle" on the scrub handle.
export default {
    components: {
        VDropdown: Dropdown,
    },
    props: {
        src: {
            type: String,
            default: null,
        },
        rewindButton: {
            type: Boolean,
            default: false,
        },
        hideRate: {
            type: Boolean,
            default: false,
        },
        book: {
            type: [String, Number],
            default: undefined,
        },
        noSaveTime: {
            type: Boolean,
            default: false,
        },
        preserveRate: {
            type: Boolean,
            default: false,
        },
        hideVolume: {
            type: Boolean,
            default: false,
        },
        paused: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            audio: null,
            playing: false,
            played: 0,
            duration: 0,
            rate: 1,
            playbackRates: PLAYBACK_RATES,
            playWasClicked: false,
            // bar
            barHovered: false,
            seekTime: 0,
            volume: 1,
            previousVolume: 1,
            rAF: undefined,
        };
    },
    computed: {
        timeString() {
            if (!this.played) return '00:00';

            return formatDuration(this.played);
        },
        durationString() {
            if (!this.duration) return '00:00';
            return formatDuration(this.duration);
        },
    },
    watch: {
        src() {
            if (this.audio) {
                this.audio.currentTime = 0;
                this.audio.load();
                this.setDuration(0);
                this.pause();
                this.update();
            }
        },
        volume(value) {
            if (this.audio) {
                this.audio.volume = value;
            }
        },
        paused(value) {
            if (value) {
                this.pause();
            }
        },
        rate(value) {
            this.$emit('rateChanged', { value: value || PLAYBACK_RATES[0] });
        },
    },
    mounted() {
        this.audio = this.$refs.audio;
        // this.audio.addEventListener('timeupdate', this.update);
        this.audio.addEventListener('ended', this.onEnded);
        this.audio.addEventListener('loadedmetadata', this.setDuration);

        if (this.book !== undefined && !this.noSaveTime) {
            this.setTimeFromLocalStorage();
        }
    },
    beforeUnmount() {
        if (this.book !== undefined && !this.noSaveTime) {
            this.saveTimeToLocalStorage();
        }
        // this.audio.removeEventListener('timeupdate', this.update);
        this.audio.removeEventListener('ended', this.onEnded);
        this.audio.removeEventListener('loadedmetadata', this.setDuration);
        window.cancelAnimationFrame(this.rAF);
        this.rAF = null;
    },
    methods: {
        play() {
            this.audio
                .play()
                .then(() => {
                    this.rAF = window.requestAnimationFrame(this.update);
                    this.playing = true;
                    this.playWasClicked = true;
                    this.$emit('playing');
                })
                .catch(() => {});
        },
        pause() {
            this.playing = false;
            this.audio.pause();
            window.cancelAnimationFrame(this.rAF);
            this.rAF = null;
            this.$emit('paused');
            if (this.book !== undefined && !this.noSaveTime) {
                this.saveTimeToLocalStorage();
            }
        },
        toggle() {
            if (this.playing) {
                this.pause();
                return;
            }

            this.play();
        },
        update() {
            this.played = this.audio.currentTime;
            this.$emit('progress', {
                current: this.audio.currentTime,
                duration: this.audio.duration,
            });

            const rect = this.$refs.bar.getBoundingClientRect();
            const percentage = this.audio.currentTime / this.audio.duration;
            this.$refs.progress.style.width = percentage * rect.width + 'px';
            this.$refs.handle.style.left = `calc(${percentage * rect.width} - 7px)`;
            if (this.playing) {
                window.cancelAnimationFrame(this.rAF);
                this.rAF = null;
                this.rAF = window.requestAnimationFrame(this.update);
            }
        },
        seek(event) {
            const relativePosition = this.calcRelativePosition(event.pageX, 'progress');
            this.audio.currentTime = this.duration * relativePosition;

            if (this.book !== undefined && !this.noSaveTime) {
                this.saveTimeToLocalStorage();
            }
        },
        toTime(time) {
            this.audio.currentTime = time;
            this.$emit('progress', {
                current: this.audio.currentTime,
                duration: this.audio.duration,
            });
        },
        calcRelativePosition(x, refName) {
            if (!this.$refs[refName]) return;

            const fullWidth = this.$refs.bar.offsetWidth;
            const offset = offsetLeft(this.$refs[refName]);
            return Math.max(0, Math.min(1, (x - offset) / fullWidth));
        },
        onEnded() {
            this.playing = false;
            this.audio.currentTime = 0;
            this.$emit('finished');
        },
        setDuration() {
            this.duration = this.audio.duration;
            if (this.preserveRate) {
                this.setRate(this.rate);
            }
        },
        setRate(rate) {
            this.rate = rate;
            this.audio.playbackRate = rate;
            this.$emit('saveRate', rate);
        },
        onBarMouseEnter(event) {
            this.barHovered = true;
            this.$refs.loaded.style.width = event.offsetX + 'px';
            this.seekTime = formatDuration(
                this.duration * this.calcRelativePosition(event.pageX, 'loaded')
            );
        },
        onBarMouseMove(event) {
            this.$refs.loaded.style.width = event.offsetX + 'px';
            this.seekTime = formatDuration(
                this.duration * this.calcRelativePosition(event.pageX, 'loaded')
            );
        },
        onBarMouseLeave() {
            this.barHovered = false;
            if (this.$refs.loaded) {
                this.$refs.loaded.style.width = 0;
            }
            this.seekTime = 0;
        },
        saveTimeToLocalStorage() {
            let key = `sp-bookplayer-${this.book}`;
            let { currentTime, duration } = this.audio;

            if (currentTime === duration) {
                localStorage.removeItem(key);
                return;
            }

            let time;

            if (this.playWasClicked) {
                time = Math.max(0, currentTime - 5);
            } else {
                time = Math.max(0, currentTime);
            }

            localStorage.setItem(key, time);
        },
        setTimeFromLocalStorage() {
            let key = `sp-bookplayer-${this.book}`;

            let number = localStorage.getItem(key);

            if (!number) return;

            number = Number(number);

            if (number >= this.audio.duration) return;

            this.audio.currentTime = number;
        },
        copySrc() {
            copyToClipboard(this.src);
        },
    },
};
